import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email || password.length < 6) {
      toast.error("Use a valid email and a password of at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const result = await signup(email, password);
      toast.success("Welcome to SafeScale — 3-day Pro trial started.");
      // Fresh signup — always route into onboarding
      if (result?.user?.has_brand && result?.user?.log_count > 0) {
        navigate("/dashboard", { replace: true });
      } else if (result?.user?.has_brand) {
        navigate("/onboarding/first-log", { replace: true });
      } else {
        navigate("/onboarding/brand", { replace: true });
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.response?.status ? `Sign-up failed (HTTP ${err.response.status})` : null) ||
        err?.message ||
        "Sign-up failed — please check your connection and try again.";
      toast.error(String(detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-md mx-auto px-4 py-16">
        <h1 className="font-display font-bold text-3xl tracking-tight text-slate-900">Create your account</h1>
        <p className="mt-2 text-slate-600 text-sm">3-day full trial. No credit card required.</p>

        <Card className="mt-8 p-6 sm:p-8 bg-white rounded-2xl border-slate-200">
          <Button
            type="button"
            onClick={handleGoogle}
            variant="outline"
            className="w-full h-11 border-slate-300 font-medium"
            data-testid="google-signin-button"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200" />
            OR
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="signup-email-input"
                  placeholder="you@brand.com"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-slate-500">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="signup-password-input"
                  placeholder="at least 6 characters"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800"
              data-testid="signup-submit-button"
            >
              {loading ? "Creating…" : (<>Create account <ArrowRight className="ml-2 h-4 w-4" /></>)}
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-slate-900 font-medium underline" data-testid="signup-to-login-link">Log in</Link>
        </p>
      </main>
    </div>
  );
}

const GoogleIcon = () => (
  <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-4.9c-2 1.4-4.4 2.3-6.9 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6 4.9c-.4.4 6.6-4.8 6.6-14.5 0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);
